import { Button, Container, Grid, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import FuelAutoComplete from '@/components/fuel-autocomplete';
import { useProgress } from '@/contexts/ProgressProvider';
import { useModalContext } from '@/modals/VNModalProvider';
import type { FuelMapperTemplate } from '@/models/fuel-mapper-template';
import { useAppDispatch } from '@/store';
import { setApplicationMessage } from '@/store/slices/application-data';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import VnModalBody from '../VnModalBody';
import VnModalFooter from '../VnModalFooter';

interface IPros {
  templates: FuelMapperTemplate[];
  // form:any;
  onUpload: (form: any) => void;
}

const Label = styled.label`
  margin-left: 5px;
`;
const FileInput = styled.input`
  display: none;
`;
const ImportModal = ({ templates, onUpload }: IPros) => {
  const [selectedTemplate, setSelectedTemplate] = useState<FuelMapperTemplate>(null);
  const fileInput = useRef(null);
  const [csvFile, setCsvFile] = useState<File>(null);
  const dispatch = useAppDispatch();
  const { confirm, alert } = useAppUtils();
  const { loading } = useProgress();
  const { onClose } = useModalContext();
  const handleClose = () => {
    // setOpen(false);
    onClose();
  };

  const onBrowseButtonClick = () => {
    // `current` points to the mounted file input element
    fileInput.current.click();
  };

  const processFileChange = (fileList: FileList) => {
    if (fileList.length === 0) return;
    const allowedFiles = ['.csv'];
    const allowedFilesExtension = /(\.csv)$/i;
    if (fileList.length === 0) return;

    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    const file = fileList.item(0);
    if (file && !allowedFilesExtension.exec(file && file.name)) {
      return;
    }
    setCsvFile(file);
  };

  const onUploadClick = async () => {
    // Todo Add validation
    if (!validateForm()) {
      await dispatch(
        setApplicationMessage({ message: 'Please fix any validation warnings before continuing.', severity: 'error' })
      );
      return;
    }
    const templateId = selectedTemplate.id;
    const obj = {
      templateId,
      csvFile,
    };
    onUpload(obj);
  };
  const validateForm = () => {
    if (selectedTemplate === null) {
      return false;
    }
    if (csvFile === null) {
      return false;
    }
    return true;
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
  };

  return (
    <div>
      <VnModalBody>
        {!loading && (
          <Container className="no-padding" section="component" maxWidth="lg">
            <form encType="multipart/form-data">
              <Grid container className="section-box" spacing={2}>
                <Grid item xs={12}>
                  <FuelAutoComplete
                    id="mapper-templates"
                    items={templates}
                    itemText="templateName"
                    labelText="Template "
                    placeHolderText="Select Template"
                    controlWidth={350}
                    multiSelect={false}
                    labelPaddingTop="-5px"
                    onChange={handleTemplateChange}
                    defaultValue={selectedTemplate ?? ''}
                    isRequired
                    hasError={selectedTemplate === null}
                  />
                  <Typography variant="inherit" className="text-danger">
                    {selectedTemplate === null && <span>Template is required</span>}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FileInput type="file" id="file" ref={fileInput} onChange={(e) => processFileChange(e.target.files)} />
                  <label htmlFor="Upload">Choose File To Upload </label>
                  <Button color="primary" variant="contained" disabled={loading} autoFocus onClick={onBrowseButtonClick}>
                    Browse
                  </Button>
                  {csvFile && <Label>{clientUtils.shortenFileName(csvFile.name, 32)}</Label>}
                  <Typography variant="inherit" className="text-danger">
                    {csvFile === null && <span>CSV file is required</span>}
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Container>
        )}
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button color="primary" variant="contained" disabled={loading} autoFocus onClick={onUploadClick}>
              Upload
            </Button>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
          </>
        }
      />
    </div>
  );
};

export default ImportModal;
